import React, { useMemo } from 'react';
import { ReactFlow, Background, Controls, MiniMap, Node, Edge } from 'reactflow';
import 'reactflow/dist/style.css';
import { Link } from 'react-router-dom';
import type { CurriculumCourse, CurriculumLevel } from '../types';

const NODE_WIDTH = 200;
const NODE_HEIGHT = 70;
const LEVEL_GAP = 120;
const NODE_GAP = 40;

interface CurriculumVisualizationProps {
  curriculumId: string;
  courses: CurriculumCourse[];
  levels: CurriculumLevel[];
}

function CourseNode({ data }: { data: { course: CurriculumCourse; curriculumId: string } }) {
  const { course, curriculumId } = data;
  const dur = course.duration
    ? course.duration >= 60
      ? `${Math.floor(course.duration / 60)} gün`
      : `${course.duration} saat`
    : null;
  return (
    <Link
      to={`/app/curricula/${curriculumId}/courses/${course.id}`}
      className="block min-w-[180px] max-w-[220px] rounded-lg border-2 border-amber-800 bg-amber-900/95 px-4 py-3 shadow-md hover:border-amber-600 hover:shadow-lg transition-all text-white"
    >
      <div className="font-medium text-sm leading-tight truncate" title={course.title}>
        {course.title}
      </div>
      {dur && <div className="text-xs text-amber-200 mt-1">{dur}</div>}
    </Link>
  );
}

function ElectiveNode({ data }: { data: { course: CurriculumCourse; curriculumId: string } }) {
  const { course, curriculumId } = data;
  const dur = course.duration
    ? course.duration >= 60
      ? `${Math.floor(course.duration / 60)} gün`
      : `${course.duration} saat`
    : null;
  return (
    <Link
      to={`/app/curricula/${curriculumId}/courses/${course.id}`}
      className="block min-w-[180px] max-w-[220px] rounded-lg border-2 border-gray-400 bg-white dark:bg-gray-800 px-4 py-3 shadow-md hover:border-primary-500 hover:shadow-lg transition-all text-gray-900 dark:text-white"
    >
      <div className="font-medium text-sm leading-tight truncate" title={course.title}>
        {course.title}
      </div>
      {dur && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{dur}</div>}
    </Link>
  );
}

const nodeTypes = { course: CourseNode, elective: ElectiveNode };

export default function CurriculumVisualization({
  curriculumId,
  courses,
  levels,
}: CurriculumVisualizationProps) {
  const { nodes, edges } = useMemo(() => {
    const sortedLevels = [...levels].sort((a, b) => a.order - b.order);
    const levelMap = new Map<string, number>();
    sortedLevels.forEach((l, i) => levelMap.set(l.id, i));

    const nodesByLevel = new Map<string, CurriculumCourse[]>();
    courses.forEach((c) => {
      const lid = c.levelId || '__unassigned__';
      if (!nodesByLevel.has(lid)) nodesByLevel.set(lid, []);
      nodesByLevel.get(lid)!.push(c);
    });
    nodesByLevel.forEach((arr) => arr.sort((a, b) => a.order - b.order));

    const nodes: Node[] = [];
    const edges: Edge[] = [];

    sortedLevels.forEach((level, levelIdx) => {
      const levelCourses = nodesByLevel.get(level.id) || [];
      const y = levelIdx * (NODE_HEIGHT + LEVEL_GAP) + 50;
      levelCourses.forEach((course, idx) => {
        const x = idx * (NODE_WIDTH + NODE_GAP) + 50;
        nodes.push({
          id: course.id,
          type: 'course',
          position: { x, y },
          data: { course, curriculumId },
        });
        (course.prerequisiteCourseIds || []).forEach((prereqId) => {
          edges.push({
            id: `${prereqId}-${course.id}`,
            source: prereqId,
            target: course.id,
            type: 'smoothstep',
          });
        });
      });
    });

    const unassigned = nodesByLevel.get('__unassigned__') || [];
    if (unassigned.length > 0) {
      const y = sortedLevels.length * (NODE_HEIGHT + LEVEL_GAP) + 50;
      unassigned.forEach((course, idx) => {
        const x = idx * (NODE_WIDTH + NODE_GAP) + 50;
        nodes.push({
          id: course.id,
          type: 'elective',
          position: { x, y },
          data: { course, curriculumId },
        });
        (course.prerequisiteCourseIds || []).forEach((prereqId) => {
          edges.push({
            id: `${prereqId}-${course.id}`,
            source: prereqId,
            target: course.id,
            type: 'smoothstep',
          });
        });
      });
    }

    return { nodes, edges };
  }, [curriculumId, courses, levels]);

  if (courses.length === 0) return null;

  return (
    <div className="card overflow-hidden">
      <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Müfredat Görünümü</h3>
      <div className="h-[400px] rounded-lg border border-gray-200 dark:border-gray-700">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          <Controls />
          <MiniMap nodeColor="#92400e" maskColor="rgba(0,0,0,0.1)" />
        </ReactFlow>
      </div>
    </div>
  );
}
